import React, { useState, useEffect } from "react";
import { PLAN_FIELD_GROUPS } from "../../utils/planSettings";
import { useModal } from "../../context/ModalContext";

const API = process.env.REACT_APP_API_URL || "";

export default function AdminPlanSettings() {
  const { showAlert } = useModal();
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const ALL_KEYS = PLAN_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));

  useEffect(() => {
    fetch(`${API}/api/app-settings`)
      .then((r) => r.json())
      .then((data) => {
        setSettings(data);
        setInitialSettings(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function saveField(key) {
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(`${API}/api/app-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: settings[key] }),
      });
      if (!res.ok) throw new Error("save failed");
      setInitialSettings((p) => ({ ...p, [key]: settings[key] }));
    } catch (e) {
      showAlert("저장 실패");
    }
    setSaving((p) => ({ ...p, [key]: false }));
  }

  function handleChange(key, value) {
    setSettings((p) => ({ ...p, [key]: value }));
  }

  function isDirty(key) {
    return (settings[key] || "") !== (initialSettings[key] || "");
  }

  const dirtyKeys = ALL_KEYS.filter((k) => isDirty(k));
  const dirtyCount = dirtyKeys.length;

  async function saveAll() {
    if (dirtyCount === 0) return;
    setSavingAll(true);
    try {
      const body = Object.fromEntries(dirtyKeys.map((k) => [k, settings[k]]));
      const res = await fetch(`${API}/api/app-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("save failed");
      setInitialSettings((p) => ({ ...p, ...body }));
    } catch (e) {
      showAlert("일괄 저장 실패");
    }
    setSavingAll(false);
  }

  function revertAll() {
    setSettings((p) => {
      const next = { ...p };
      dirtyKeys.forEach((k) => { next[k] = initialSettings[k] || ""; });
      return next;
    });
  }

  if (loading) return <div className="admin-print-loading">로딩 중...</div>;

  return (
    <div className="admin-print-settings">
      <h1>요금제 설정</h1>
      <p className="admin-print-sub">요금제 가격 및 한도 관리 (요금제 페이지에 자동 반영)</p>

      {PLAN_FIELD_GROUPS.map((group) => (
        <section key={group.title} className="aps-section">
          <div className="aps-section-header">
            <h2>{group.title}</h2>
          </div>
          {group.fields.map((f) => (
            <div className="aps-row" key={f.key}>
              <div className="aps-label">
                <span className="aps-label-text">{f.label}</span>
                <span className="aps-label-key">{f.key}</span>
              </div>
              <div className="aps-input-wrap">
                <input
                  type="text"
                  className={isDirty(f.key) ? "aps-input-dirty" : ""}
                  value={settings[f.key] || ""}
                  placeholder={f.placeholder}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                />
                <button
                  className={isDirty(f.key) ? "aps-save-dirty" : ""}
                  onClick={() => saveField(f.key)}
                  disabled={saving[f.key]}
                >
                  {saving[f.key] ? "..." : "저장"}
                </button>
              </div>
            </div>
          ))}
        </section>
      ))}

      <div style={{ marginTop: 20, padding: 16, background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8, fontSize: 13, color: "#5d4037" }}>
        ⚠️ <strong>참고:</strong> 가격을 변경해도 PayPal 구독 플랜은 자동 업데이트되지 않습니다.
        실제 결제 금액을 변경하려면 PayPal 개발자 대시보드에서 새 Plan을 만들고 .env의
        REACT_APP_PAYPAL_PLAN_ID_STANDARD / REACT_APP_PAYPAL_PLAN_ID_PRO를 업데이트해야 합니다.
        여기 입력한 가격은 사용자에게 표시되는 가격일 뿐입니다.
      </div>

      <div className={`aps-bulk-bar${dirtyCount > 0 ? " aps-bulk-dirty" : ""}`}>
        <div className="aps-bulk-info">
          {dirtyCount > 0 ? (
            <>변경된 항목 <strong>{dirtyCount}</strong>개</>
          ) : (
            "변경된 항목 없음"
          )}
        </div>
        <div className="aps-bulk-actions">
          <button onClick={revertAll} disabled={dirtyCount === 0 || savingAll}>
            되돌리기
          </button>
          <button
            className="aps-bulk-save"
            onClick={saveAll}
            disabled={dirtyCount === 0 || savingAll}
          >
            {savingAll ? "저장 중..." : `일괄 저장${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
